import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, DollarSign } from 'lucide-react';
import api from '@/api/api';
const OtherFeeMaster = () => {
  const [feeTypes, setFeeTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedFeeType, setSelectedFeeType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_amount: 0
  });

  useEffect(() => {
    fetchFeeTypes();
  }, []);

  const fetchFeeTypes = async () => {
    try {
      const response = await api.get('/other-fees/fee-types');
      setFeeTypes(response.data);
    } catch (error) {
      console.error('Error fetching fee types:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editMode && selectedFeeType) {
        await api.put(`/other-fees/fee-types/${selectedFeeType.id}`, formData);
        alert('Fee type updated successfully!');
      } else {
        await api.post('/other-fees/fee-types', formData);
        alert('Fee type created successfully!');
      }

      setShowModal(false);
      setFormData({ name: '', description: '', default_amount: 0 });
      setEditMode(false);
      setSelectedFeeType(null);
      fetchFeeTypes();
    } catch (error) {
      console.error('Error saving fee type:', error);
      alert(error.response?.data?.detail || 'Failed to save fee type');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (feeType) => {
    setSelectedFeeType(feeType);
    setFormData({
      name: feeType.name,
      description: feeType.description || '',
      default_amount: feeType.default_amount
    });
    setEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (feeTypeId) => {
    if (!window.confirm('Are you sure you want to delete this fee type?')) return;

    try {
      await api.delete(`/other-fees/fee-types/${feeTypeId}`);
      alert('Fee type deleted successfully!');
      fetchFeeTypes();
    } catch (error) {
      console.error('Error deleting fee type:', error);
      alert('Failed to delete fee type');
    }
  };

  const handleAddNew = () => {
    setFormData({ name: '', description: '', default_amount: 0 });
    setEditMode(false);
    setSelectedFeeType(null);
    setShowModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Other Fee Master</h1>
          <p className="text-sm text-gray-500 mt-1">Manage other fee types (Library, Lab, Hostel, etc.)</p>
        </div>
        <button
          onClick={handleAddNew}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Fee Type
        </button>
      </div>

      {/* Fee Types List */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fee Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Default Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {feeTypes.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                    <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    No fee types created yet
                  </td>
                </tr>
              ) : (
                feeTypes.map((feeType) => (
                  <tr key={feeType.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{feeType.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{feeType.description || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">₹{feeType.default_amount.toLocaleString('en-IN')}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(feeType)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(feeType.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? 'Edit Fee Type' : 'Add Fee Type'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fee Type Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Library Fee, Lab Fee"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Optional description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Amount (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={formData.default_amount}
                  onChange={(e) => setFormData({ ...formData, default_amount: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Saving...' : editMode ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtherFeeMaster;
